// Hooks de datos de V360 sobre el cliente RPC tipado. Los tipos de respuesta
// salen del Worker (InferResponseType), así que la SPA no duplica contratos.
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { InferResponseType } from "hono/client";
import { useToast } from "@iavmx/ui";
import { leer, type Api } from "../api";
import { useSesion } from "../sesion";

type Rutas = Api["api"]["campanas"];
export type Catalogo = InferResponseType<Rutas["catalogo"]["$get"], 200>;
export type Bandeja = InferResponseType<Rutas["$get"], 200>;
export type FilaBandeja = Bandeja["filas"][number];
export type Calendario = InferResponseType<Rutas["calendario"]["$get"], 200>;
export type Historial = InferResponseType<Rutas["historial"]["$get"], 200>;
export type Avisos = InferResponseType<Rutas["avisos"]["$get"], 200>;
export type Ficha = InferResponseType<Rutas[":id"]["$get"], 200>;
export type TareaFicha = Ficha["tareas"][number];
export type Solicitud = InferResponseType<Api["api"]["solicitudes"][":id"]["$get"], 200>;
export type Estructurada = InferResponseType<Api["api"]["solicitudes"]["estructurar"]["$post"], 200>;
export type Propuesta = Estructurada["propuesta"];
export type Desglose = InferResponseType<Rutas[":id"]["tareas"]["proponer"]["$post"], 200>;

/** ¿Hay con quién hablar? Con sesión o en modo local. */
export function useConSesion() {
  const { correo, persona, configuracion } = useSesion();
  return Boolean(correo || persona || configuracion?.modoLocal);
}

export function useCatalogo() {
  const { api } = useSesion();
  const listo = useConSesion();
  return useQuery({
    queryKey: ["catalogo"],
    queryFn: () => api.api.campanas.catalogo.$get().then((r) => leer<Catalogo>(r)),
    enabled: listo,
    staleTime: 5 * 60_000,
  });
}

export function useBandeja(filtros: { estado?: string; semaforo?: string; q?: string } = {}) {
  const { api } = useSesion();
  const listo = useConSesion();
  const query: Record<string, string> = {};
  if (filtros.estado) query.estado = filtros.estado;
  if (filtros.semaforo) query.semaforo = filtros.semaforo;
  if (filtros.q) query.q = filtros.q;
  return useQuery({
    queryKey: ["bandeja", query],
    queryFn: () => api.api.campanas.$get({ query }).then((r) => leer<Bandeja>(r)),
    enabled: listo,
  });
}

export function useCalendario(semana?: string) {
  const { api } = useSesion();
  const listo = useConSesion();
  return useQuery({
    queryKey: ["calendario", semana ?? "hoy"],
    queryFn: () =>
      api.api.campanas.calendario.$get({ query: semana ? { semana } : {} }).then((r) => leer<Calendario>(r)),
    enabled: listo,
    // Al cambiar de semana se queda la anterior en pantalla hasta que llegue la nueva (sin esqueleto).
    placeholderData: keepPreviousData,
  });
}

export function useHistorial() {
  const { api } = useSesion();
  const listo = useConSesion();
  return useQuery({
    queryKey: ["historial"],
    queryFn: () => api.api.campanas.historial.$get({ query: {} }).then((r) => leer<Historial>(r)),
    enabled: listo,
  });
}

export function useAvisos() {
  const { api } = useSesion();
  const listo = useConSesion();
  return useQuery({
    queryKey: ["avisos"],
    queryFn: () => api.api.campanas.avisos.$get({ query: {} }).then((r) => leer<Avisos>(r)),
    enabled: listo,
  });
}

export function useFicha(id: string) {
  const { api } = useSesion();
  const listo = useConSesion();
  return useQuery({
    queryKey: ["campana", id],
    queryFn: () => api.api.campanas[":id"].$get({ param: { id } }).then((r) => leer<Ficha>(r)),
    enabled: listo,
  });
}

export function useSolicitud(id: string) {
  const { api } = useSesion();
  const listo = useConSesion();
  return useQuery({
    queryKey: ["solicitud", id],
    queryFn: () => api.api.solicitudes[":id"].$get({ param: { id } }).then((r) => leer<Solicitud>(r)),
    enabled: listo,
  });
}

/**
 * Mutación con aviso de éxito o error y refresco de las consultas que toca.
 * `fn` recibe el cliente y devuelve la Response de Hono.
 */
export function useAccion<TVars, TResp = unknown>(opciones: {
  fn: (api: Api, vars: TVars) => Promise<Response>;
  invalidar?: (string | readonly unknown[])[];
  exito?: string | ((r: TResp, vars: TVars) => string | null);
  onExito?: (r: TResp, vars: TVars) => void;
}) {
  const { api } = useSesion();
  const qc = useQueryClient();
  const { avisar } = useToast();
  return useMutation({
    mutationFn: async (vars: TVars) => leer<TResp>(await opciones.fn(api, vars)),
    onSuccess: (r, vars) => {
      for (const k of opciones.invalidar ?? [])
        void qc.invalidateQueries({ queryKey: typeof k === "string" ? [k] : [...k] });
      const msg = typeof opciones.exito === "function" ? opciones.exito(r, vars) : opciones.exito;
      if (msg) avisar({ titulo: msg, tono: "exito" });
      opciones.onExito?.(r, vars);
    },
    onError: (e: Error) => avisar({ titulo: "No se pudo", descripcion: e.message, tono: "error" }),
  });
}

/** ¿La persona en sesión es de Mercadotecnia (o Dirección/admin)? Define qué acciones ve. */
export function useEsMercadotecnia() {
  const { persona } = useSesion();
  if (!persona) return false;
  return persona.rol === "admin" || persona.rol === "direccion" || persona.areas.includes("mercadotecnia");
}
